import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { useFecharAoClicarFora } from './fechar-fora'

/**
 * As peças básicas da interface.
 *
 * O desenho anterior contornava tudo: cartão, campo, botão, cabeçalho e cada
 * linha de tabela tinham a mesma borda cinza de 1px. Numa tela com quinze
 * elementos são quinze retângulos iguais competindo com o conteúdo, e nenhuma
 * hierarquia sai disso — foi o que envelheceu a interface.
 *
 * A regra agora: **sombra eleva, borda separa**. Cartão e janela sobem por
 * sombra; borda fica onde ela responde "onde eu digito" (campo) ou "onde uma
 * coisa acaba e a outra começa" (divisor de lista).
 *
 * O desenho é o do site FaroEscolar (ADR 0055): botão em pílula, cantos
 * generosos, títulos em Montserrat e a faixa azul, turquesa e verde da marca
 * no alto das janelas.
 */

export function juntar(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ')
}

interface BotaoProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: 'primario' | 'neutro' | 'perigo' | 'texto'
  carregando?: boolean
}

export function Botao({
  variante = 'neutro',
  carregando,
  className,
  children,
  disabled,
  ...resto
}: BotaoProps) {
  const estilos = {
    /*
      O primário é o único com peso visual de verdade. Quando três botões numa
      barra têm o mesmo destaque, nenhum é o destaque — e o olho tem de ler os
      três para achar a ação principal.
    */
    primario:
      'bg-marca text-white shadow-media shadow-marca/25 hover:bg-marca-escura active:scale-[0.98]',
    neutro:
      'bg-superficie text-tinta-800 shadow-suave ring-1 ring-tinta-200 hover:bg-tinta-50 hover:text-tinta-900 hover:ring-tinta-300 active:scale-[0.98]',
    perigo: 'bg-superficie text-red-700 shadow-suave ring-1 ring-red-200 hover:bg-red-50',
    texto: 'text-tinta-600 hover:bg-tinta-100 hover:text-tinta-900',
  }[variante]

  return (
    <button
      type="button"
      disabled={disabled || carregando}
      className={juntar(
        'inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold',
        // A transição cobre cor e escala: o `active:scale` sem transição vira
        // um salto, e com ela vira o toque que dá sensação de botão físico.
        'transition duration-150',
        'disabled:pointer-events-none disabled:opacity-40',
        estilos,
        className,
      )}
      {...resto}
    >
      {carregando ? 'Aguarde…' : children}
    </button>
  )
}

interface CampoProps extends InputHTMLAttributes<HTMLInputElement> {
  rotulo: string
  erro?: string
  dica?: string
}

export function Campo({ rotulo, erro, dica, className, ...resto }: CampoProps) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-tinta-700">{rotulo}</span>
      <input
        className={juntar(
          'w-full rounded-lg bg-superficie px-3.5 py-2.5 text-sm text-tinta-900 outline-none',
          // Anel em vez de borda: `ring` não ocupa espaço no layout, então o
          // campo não "cresce" um pixel ao ganhar foco nem ao dar erro.
          'ring-1 transition',
          'placeholder:text-tinta-400',
          erro
            ? 'ring-red-400 focus:ring-2 focus:ring-red-500'
            : 'ring-tinta-200 hover:ring-tinta-300 focus:ring-2 focus:ring-marca/60',
          className,
        )}
        {...resto}
      />
      {erro ? <span className="mt-1 block text-xs text-red-600">{erro}</span> : null}
      {dica && !erro ? <span className="mt-1 block text-xs text-tinta-400">{dica}</span> : null}
    </label>
  )
}

export function Cartao({
  titulo,
  descricao,
  acoes,
  children,
}: {
  titulo?: string
  descricao?: string
  acoes?: ReactNode
  /**
   * Sem conteúdo, o cartão é só o cabeçalho: abre uma aba cujo miolo vem
   * logo abaixo, em cartões próprios (as pastas de Materiais).
   */
  children?: ReactNode
}) {
  const temCorpo = children !== undefined && children !== null && children !== false
  return (
    // No papel o cartão não se parte entre duas folhas, e a sombra vira linha.
    <section
      className="overflow-hidden rounded-2xl bg-superficie shadow-suave ring-1 ring-tinta-200/60 print:break-inside-avoid print:shadow-none print:ring-1 print:ring-tinta-200"
    >
      {(titulo || acoes) && (
        /*
          O cabeçalho não tem mais linha divisória.

          O espaço já separa, e a linha era mais uma das quinze do exemplo lá
          em cima. Onde o conteúdo encosta no título — tabela, lista — quem põe
          o divisor é o próprio conteúdo, que sabe onde ele faz falta.
        */
        <header
          className={juntar(
            'flex items-start justify-between gap-4 px-6 pt-5',
            temCorpo ? 'pb-3' : 'pb-4',
          )}
        >
          <div className="min-w-0">
            {titulo ? (
              <h2 className="text-base font-bold text-tinta-900">{titulo}</h2>
            ) : null}
            {descricao ? <p className="mt-0.5 text-sm text-tinta-500">{descricao}</p> : null}
          </div>
          {/* Botão não se aperta no papel. */}
          {acoes ? <div className="flex shrink-0 gap-2 print:hidden">{acoes}</div> : null}
        </header>
      )}
      {temCorpo ? (
        <div className={juntar('px-6', titulo || acoes ? 'pb-6' : 'py-6')}>{children}</div>
      ) : null}
    </section>
  )
}

export function Aviso({
  tipo = 'info',
  children,
}: {
  tipo?: 'info' | 'erro' | 'sucesso' | 'atencao'
  children: ReactNode
}) {
  /*
    A faixa colorida à esquerda faz o trabalho que a borda inteira fazia, e
    melhor: dá para reconhecer o tipo do aviso pelo canto do olho, sem ler.
    O fundo continua claro para o texto manter contraste alto.
  */
  const estilos = {
    info: 'bg-tinta-50 text-tinta-700 border-tinta-300',
    erro: 'bg-red-50 text-red-800 border-red-500',
    sucesso: 'bg-green-50 text-green-800 border-green-500',
    atencao: 'bg-amber-50 text-amber-900 border-amber-500',
  }[tipo]

  return (
    <div className={juntar('rounded-xl border-l-4 px-4 py-3 text-sm', estilos)}>
      {children}
    </div>
  )
}

export function Modal({
  aberto,
  titulo,
  aoFechar,
  children,
  rodape,
  fecharAoClicarFora = true,
  largura = 'normal',
}: {
  aberto: boolean
  titulo: string
  aoFechar: () => void
  children: ReactNode
  rodape?: ReactNode
  /**
   * `larga` para grade com várias colunas de caixas, como a de permissões.
   * Na largura normal as ações de cada tela quebravam em duas linhas e a grade
   * perdia o alinhamento que faz dela uma grade.
   */
  largura?: 'normal' | 'larga'
  /**
   * Clicar fora fecha, e Esc também. Vale para tudo por padrão, porque caçar o
   * X é o incômodo mais comum.
   *
   * Passe `false` em janela de formulário longo, onde um clique fora sem
   * querer jogaria fora o que a pessoa acabou de digitar.
   */
  fecharAoClicarFora?: boolean
}) {
  // O gancho precisa ser chamado sempre, então vem antes de qualquer retorno.
  const painel = useFecharAoClicarFora<HTMLDivElement>(aberto && fecharAoClicarFora, aoFechar)

  if (!aberto) return null

  return (
    /*
      O fundo escurece e desfoca o que está atrás.

      Só escurecer deixa a tela de trás legível o bastante para o olho tentar
      lê-la; o desfoque resolve isso sem precisar de um véu mais escuro, que
      faria a janela parecer um alerta de erro.

      A janela não vai para o `body` (janela dentro de janela depende de estar
      dentro da outra para o clique não fechar a de fora), então ela zera o
      texto que herdaria de onde foi montada: tamanho, cor, peso, alinhamento
      e quebra de linha. Montada numa linha miúda e sem quebra, ela saía
      cortada numa linha só (06/10/2026, a pergunta dos quadradinhos do funil).
    */
    <div className="fixed inset-0 z-50 flex cursor-auto items-center justify-center whitespace-normal bg-fundo-escuro/50 p-4 text-left text-base font-normal not-italic text-tinta-900 normal-case tracking-normal backdrop-blur-sm">
      <div
        ref={painel}
        role="dialog"
        aria-label={titulo}
        className={juntar(
          'flex max-h-[85vh] w-full flex-col overflow-hidden rounded-3xl bg-superficie shadow-alta',
          largura === 'larga' ? 'max-w-3xl' : 'max-w-lg',
        )}
      >
        {/* A faixa da marca, como no alto dos cartões do site (ADR 0055). */}
        <div aria-hidden className="h-1.5 shrink-0 bg-linear-to-r from-marca via-faro-turquesa to-faro-verde" />
        <header className="flex shrink-0 items-center justify-between gap-4 px-6 pb-3 pt-5">
          <h2 className="text-lg font-bold text-tinta-900">{titulo}</h2>
          <button
            type="button"
            onClick={aoFechar}
            className="-mr-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg text-tinta-400 transition hover:bg-tinta-100 hover:text-tinta-900"
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">{children}</div>

        {rodape ? (
          /*
            O rodapé tem fundo próprio em vez de linha.

            Ele fica colado depois de um conteúdo que rola: a linha sozinha
            some entre o texto e os botões, e o degrau de cor deixa claro que
            ali termina o que se lê e começa o que se decide.

            Quebra linha quando não cabe (celular): sem isso, os botões da
            esquerda e os da direita se sobrepunham na janela de permissões.
          */
          <footer className="flex shrink-0 flex-wrap justify-end gap-2 bg-tinta-50 px-6 py-4">
            {rodape}
          </footer>
        ) : null}
      </div>
    </div>
  )
}

export function Carregando({ texto = 'Carregando…' }: { texto?: string }) {
  return (
    <div className="flex items-center justify-center gap-2.5 py-10 text-sm text-tinta-400">
      {/*
        O texto sozinho não distingue "carregando" de "vazio" — os dois são uma
        frase cinza no meio da tela. O giro diz que algo está acontecendo.
      */}
      <span
        aria-hidden="true"
        className="h-4 w-4 animate-spin rounded-full border-2 border-tinta-200 border-t-marca"
      />
      {texto}
    </div>
  )
}

export function Etiqueta({
  children,
  cor = 'neutra',
}: {
  children: ReactNode
  cor?: 'neutra' | 'verde' | 'ambar' | 'vermelha' | 'azul'
}) {
  /*
    Fundo bem claro com texto escuro da mesma família, em vez do par
    100/800 anterior — que ficava pesado quando havia três ou quatro etiquetas
    na mesma linha, e é o caso normal num cartão de conversa.
  */
  const estilos = {
    neutra: 'bg-tinta-100 text-tinta-600 ring-tinta-200',
    verde: 'bg-green-50 text-green-700 ring-green-200',
    ambar: 'bg-amber-50 text-amber-700 ring-amber-200',
    vermelha: 'bg-red-50 text-red-700 ring-red-200',
    azul: 'bg-marca-clara text-blue-800 ring-blue-200',
  }[cor]

  return (
    <span
      className={juntar(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset',
        estilos,
      )}
    >
      {children}
    </span>
  )
}
